/**
 * Date locale registration for react-datepicker.
 *
 * react-datepicker delegates calendar text (month names, weekday headers,
 * day-of-week ordering, etc.) to date-fns Locale objects. Each locale has
 * to be registered with react-datepicker once at module load. The picker
 * then accepts the registered key via its `locale` prop.
 *
 * One registration per supported i18n language. Map keys match the i18n
 * short codes from src/i18n/i18n.tsx; fallback is 'en' (en-GB).
 *
 * If the supported-language list in i18n.tsx grows, add the corresponding
 * date-fns locale here too.
 */
import i18n from 'i18next';
import { registerLocale } from 'react-datepicker';

import enGB from 'date-fns/locale/en-GB';
import sv from 'date-fns/locale/sv';
import nb from 'date-fns/locale/nb';
import es from 'date-fns/locale/es';
import nl from 'date-fns/locale/nl';
import de from 'date-fns/locale/de';
import fi from 'date-fns/locale/fi';
import da from 'date-fns/locale/da';
import he from 'date-fns/locale/he';

const DATE_FNS_LOCALES: Record<string, Locale> = {
  en: enGB,
  sv,
  nb,
  es,
  nl,
  de,
  fi,
  da,
  he,
};

// Side effect: register all locales once on first import. Idempotent —
// react-datepicker's registerLocale overwrites the same key without error.
Object.entries(DATE_FNS_LOCALES).forEach(([code, locale]) => {
  registerLocale(code, locale);
});

/**
 * Returns the registered react-datepicker locale key for the current
 * i18n language. Falls back to 'en' (en-GB) if the language has no
 * registered locale.
 */
export function getReactDatepickerLocale(): string {
  const lng = (i18n.language || 'en').split('-')[0];
  return DATE_FNS_LOCALES[lng] ? lng : 'en';
}
